import { useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Switch,
  Text,
  TextInput,
} from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'
import { IconAlertCircle, IconDeviceFloppy } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import cronstrue from 'cronstrue'
import dayjs from 'dayjs'
import { useOrpc } from '@/hooks/useClient'
import type { OrpcClient } from '@/orpc/client'
import { TaskArgsEditor, type ArgsEditorState } from './TaskArgsEditor'
import { FOLLOWUP_ACTION_OPTIONS, type TaskInfo } from './hooks'

type UpdateTaskInput = Parameters<OrpcClient['updateTask']>[0]

const MAX_RUNS = 10

/** Mantine v8 DateTimePicker works with local 'YYYY-MM-DD HH:mm:ss' strings (or null). */
function isoToPickerValue(iso: string | null): string | null {
  return iso ? dayjs(iso).format('YYYY-MM-DD HH:mm:ss') : null
}

function pickerValueToIso(value: string | null): string | null {
  return value ? new Date(value.replace(' ', 'T')).toISOString() : null
}

/**
 * Admin edit of a task's admin-owned fields (slug/name shown read-only — they're
 * code-owned). Live cron validation with a human-readable interpretation; scheduled args
 * via the task's registered component or the JSON fallback. Save sends only the fields
 * that actually changed, so an untouched next_run_at gets recomputed by the server when
 * the schedule changes, while an explicitly edited one wins.
 */
export function EditTaskModal({ task, onClose }: { task: TaskInfo; onClose: () => void }) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()

  const [status, setStatus] = useState<TaskInfo['status']>(task.status)
  const [logLevel, setLogLevel] = useState<TaskInfo['logLevel']>(task.logLevel)
  const [onSchedule, setOnSchedule] = useState(task.onSchedule)
  const [schedule, setSchedule] = useState(task.schedule ?? '')
  const [nextRunAt, setNextRunAt] = useState<string | null>(isoToPickerValue(task.nextRunAt))
  const [maxConcurrent, setMaxConcurrent] = useState<number | string>(task.maxConcurrentRuns)
  const [maxInstance, setMaxInstance] = useState<number | string>(task.maxInstanceRuns)
  const [followupAction, setFollowupAction] = useState<TaskInfo['followupAction']>(
    task.followupAction,
  )
  const [args, setArgs] = useState<ArgsEditorState>({ value: undefined, error: null })

  const cron = useMemo((): { description: string | null; error: string | null } => {
    const text = schedule.trim()
    if (text === '') return { description: null, error: null }
    try {
      return { description: cronstrue.toString(text), error: null }
    } catch (err) {
      // cronstrue throws plain strings for parse failures.
      return {
        description: null,
        error: typeof err === 'string' ? err : 'Invalid cron expression',
      }
    }
  }, [schedule])

  const concurrencyValid =
    typeof maxConcurrent === 'number' &&
    Number.isInteger(maxConcurrent) &&
    maxConcurrent >= 0 &&
    maxConcurrent <= MAX_RUNS &&
    typeof maxInstance === 'number' &&
    Number.isInteger(maxInstance) &&
    maxInstance >= 0 &&
    maxInstance <= MAX_RUNS

  // Only changed fields ride the PATCH: an untouched next_run_at must stay absent so the
  // server can recompute it from a schedule edit instead of receiving the stale value.
  const payload = useMemo((): UpdateTaskInput => {
    const changes: UpdateTaskInput = { slug: task.slug }
    if (status !== task.status) changes.status = status
    if (logLevel !== task.logLevel) changes.logLevel = logLevel
    if (onSchedule !== task.onSchedule) changes.onSchedule = onSchedule
    const scheduleValue = schedule.trim() === '' ? null : schedule.trim()
    if (scheduleValue !== (task.schedule ?? null)) changes.schedule = scheduleValue
    if (typeof maxConcurrent === 'number' && maxConcurrent !== task.maxConcurrentRuns) {
      changes.maxConcurrentRuns = maxConcurrent
    }
    if (typeof maxInstance === 'number' && maxInstance !== task.maxInstanceRuns) {
      changes.maxInstanceRuns = maxInstance
    }
    if (followupAction !== task.followupAction) changes.followupAction = followupAction
    if (nextRunAt !== isoToPickerValue(task.nextRunAt)) {
      changes.nextRunAt = pickerValueToIso(nextRunAt)
    }
    const argsValue = args.value ?? null
    if (JSON.stringify(argsValue) !== JSON.stringify(task.scheduledArgs ?? null)) {
      changes.scheduledArgs = argsValue
    }
    return changes
  }, [
    task,
    status,
    logLevel,
    onSchedule,
    schedule,
    nextRunAt,
    maxConcurrent,
    maxInstance,
    followupAction,
    args,
  ])

  const isDirty = Object.keys(payload).length > 1 // beyond the slug key

  const updateTask = useMutation(
    orpc.updateTask.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.listTasks.key() })
        onClose()
      },
    }),
  )

  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      title={
        <div className="flex items-baseline gap-2">
          <Text fw={600}>Edit {task.name}</Text>
          <Text size="sm" c="dimmed" ff="monospace">
            {task.slug}
          </Text>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Group grow>
          <Select
            label="Status"
            data={[
              { value: 'active', label: 'active' },
              { value: 'disabled', label: 'disabled — launches nothing' },
            ]}
            value={status}
            onChange={(value) => value && setStatus(value as TaskInfo['status'])}
            allowDeselect={false}
          />
          <Select
            label="Log level"
            data={['trace', 'debug', 'info', 'warn', 'error']}
            value={logLevel}
            onChange={(value) => value && setLogLevel(value as TaskInfo['logLevel'])}
            allowDeselect={false}
          />
        </Group>
        <Group grow>
          <NumberInput
            label="Max concurrent runs"
            description="System-wide, across all instances (0 pauses the task)"
            min={0}
            max={MAX_RUNS}
            step={1}
            allowDecimal={false}
            value={maxConcurrent}
            onChange={setMaxConcurrent}
          />
          <NumberInput
            label="Max instance runs"
            description="Per middleware instance"
            min={0}
            max={MAX_RUNS}
            step={1}
            allowDecimal={false}
            value={maxInstance}
            onChange={setMaxInstance}
          />
        </Group>
        <Select
          label="Follow-up action"
          description="Default for this task's runs: what happens to the follow-up tasks a run returns"
          data={FOLLOWUP_ACTION_OPTIONS}
          value={followupAction}
          onChange={(value) => value && setFollowupAction(value as TaskInfo['followupAction'])}
          allowDeselect={false}
        />
        <Switch
          label="Run on schedule"
          checked={onSchedule}
          onChange={(event) => setOnSchedule(event.currentTarget.checked)}
        />
        <TextInput
          label="Schedule (cron, UTC)"
          placeholder="30 4 * * *"
          classNames={{ input: 'font-mono' }}
          value={schedule}
          onChange={(event) => setSchedule(event.currentTarget.value)}
          error={cron.error}
          description={cron.description ?? 'Kept even while scheduling is off'}
        />
        <DateTimePicker
          label="Next run"
          description="Clear to let the scheduler recompute it from the cron"
          clearable
          highlightToday
          value={nextRunAt}
          onChange={setNextRunAt}
          valueFormat="MM/DD/YYYY, hh:mm A"
          timePickerProps={{
            format: '12h',
            withDropdown: true,
            popoverProps: { withinPortal: false },
          }}
        />
        <TaskArgsEditor
          slug={task.slug}
          label="Scheduled args (JSON)"
          initialValue={task.scheduledArgs}
          onChange={setArgs}
        />
        {updateTask.error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {updateTask.error.message}
          </Alert>
        )}
        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            leftSection={<IconDeviceFloppy size={14} />}
            disabled={!isDirty || args.error !== null || cron.error !== null || !concurrencyValid}
            loading={updateTask.isPending}
            onClick={() => updateTask.mutate(payload)}
          >
            Save
          </Button>
        </Group>
      </div>
    </Modal>
  )
}
